import { OrderLimitType } from '@/constants/enum';
import { time24h } from '@/helpers';
import {
  deliverySettingSelector,
  deliveryWeeklyWorkingDaySelector,
  handleSaveDeliveryWeeklyWorkingDays,
} from '@/redux/slice/delivery.slice';
import { ExactTimeType } from '@/types';
import { time24hTo12h } from '@/utils';
import { Button, InlineGrid, InlineStack, Pagination, TextField } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TimeSlots from './TimeSlots';
import { IWeeklyWorkingDay } from '@/types/components/day';

type ExactTime = {
  id: string;
  value: string;
  orderLimit: number;
}

const TimeSlotSection = ({ selectedWorkingDay }: { selectedWorkingDay: IWeeklyWorkingDay }) => {
  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryWeeklyWorkingDays = useSelector(deliveryWeeklyWorkingDaySelector);
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalItems: 0,
  });

  const timeOptions = useMemo(() => {
    return time24h.map((item) => {
      return {
        label: time24hTo12h(item.value),
        value: item.value,
      };
    });
  }, []);

  const exactTimes = useMemo(
    () =>
      (selectedWorkingDay?.exactTimes || []).map((item) => ({
        id: item.start,
        value: time24hTo12h(item.start),
        orderLimit: item.orderLimit,
      })),
    [selectedWorkingDay?.exactTimes],
  );

  useEffect(() => {
    setPagination((prev) => {
      return {
        ...prev,
        totalItems: exactTimes.length,
      };
    });
  }, [exactTimes]);

  const handleRemoveTimeSlot = (id: string) => {
    dispatch(
      handleSaveDeliveryWeeklyWorkingDays(
        deliveryWeeklyWorkingDays.map((item) => {
          if (item.day === selectedWorkingDay?.day) {
            return {
              ...item,
              exactTimes: item.exactTimes.filter((item) => item.start !== id),
            };
          }
          return item;
        }),
      ),
    );
  };

  const handleChangeOrderLimit = (value: string, index: number) => {
    dispatch(
      handleSaveDeliveryWeeklyWorkingDays(
        deliveryWeeklyWorkingDays.map((item) => {
          if (item.day === selectedWorkingDay?.day) {
            return {
              ...item,
              exactTimes: item.exactTimes?.map((item: ExactTimeType, idx: number) => {
                if (idx === index) {
                  return { ...item, orderLimit: Number(value) };
                }
                return item;
              }),
            };
          }
          return item;
        }),
      ),
    );
  };

  const currentPageTimes = useMemo(() => {
    const start = (pagination.page - 1) * pagination.perPage;
    const end = start + pagination.perPage;

    return exactTimes.slice(start, end) ? exactTimes.slice(start, end) : exactTimes;
  }, [exactTimes, pagination]);

  useEffect(() => {
    if (currentPageTimes.length === 0 && exactTimes.length > 0) {
      setPagination((prev) => ({
        ...prev,
        page: Math.ceil(exactTimes.length / prev.perPage),
      }));
    }
  }, [currentPageTimes, exactTimes.length]);

  return (
    <>
      {currentPageTimes.map((item: ExactTime, index: number) => (
        <InlineGrid
          columns={deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_TIMESLOT ? '1fr 1fr auto' : '1fr auto'}
          gap="200"
          key={item.id}
        >
          <TimeSlots
            timeOptions={timeOptions.filter(
              (option) => option.value === item.id || !exactTimes.some((time) => time.id === option.value),
            )}
            timeSlotValue={item.id}
            selectedWorkingDay={selectedWorkingDay}
            index={index}
          />
          {deliverySettings.orderLimitType === OrderLimitType.LIMIT_PER_TIMESLOT && (
            <TextField
              type="number"
              label="Order limit"
              labelHidden
              autoComplete="off"
              value={item.orderLimit.toString()}
              onChange={(value) => handleChangeOrderLimit(value, index)}
            />
          )}
          <Button
            variant="plain"
            tone="critical"
            icon={DeleteIcon}
            disabled={exactTimes.length <= 1}
            onClick={() => handleRemoveTimeSlot(item.id)}
          />
        </InlineGrid>
      ))}
      <InlineStack blockAlign="center" align="center">
        <Pagination
          hasNext={exactTimes.length > pagination.page * pagination.perPage}
          onNext={() =>
            setPagination((prev) => ({
              ...prev,
              page: prev.page + 1,
            }))
          }
          hasPrevious={pagination.page > 1}
          onPrevious={() =>
            setPagination((prev) => ({
              ...prev,
              page: prev.page - 1,
            }))
          }
          label={`${pagination.page} of ${Math.ceil(exactTimes.length / pagination.perPage)}`}
        />
      </InlineStack>
    </>
  );
};

export default TimeSlotSection;
